/**
 * Morph — animates between two RoundedPolygon shapes.
 * Ported from androidx.graphics.shapes.Morph.kt
 *
 * The Morph class pre-computes a list of matched cubic pairs at construction time.
 * Animating between shapes is then a simple per-pair linear interpolation.
 *
 * @example
 * const morph = new Morph(MD3Shapes.circle, MD3Shapes.flower);
 * const cubics = morph.asCubics(0.5); // halfway between circle and flower
 *
 * @example
 * // Zero-allocation iteration (for animation loops)
 * morph.forEachCubic(progress, (c) => {
 *   ctx.bezierCurveTo(c.control0X, c.control0Y, c.control1X, c.control1Y, c.anchor1X, c.anchor1Y);
 * });
 */

import type { RoundedPolygon } from "../core/rounded-polygon";
import { Cubic, MutableCubic } from "../math/cubic";
import { ANGLE_EPSILON, interpolate, positiveModulo } from "../utils/utils";
import { featureMapper } from "./feature-mapping";
import { MeasuredPolygon } from "./polygon-measure";

/** A matched pair of cubics (start shape, end shape). */
export type CubicPair = readonly [Cubic, Cubic];

/**
 * Animates between two RoundedPolygon shapes.
 *
 * At construction time, Morph matches the cubic curves of both shapes using
 * the featureMapper algorithm. This produces a list of CubicPairs, which can
 * then be interpolated at any progress value.
 */
export class Morph {
	/** Pre-computed list of matched cubic pairs. @internal */
	private readonly morphMatch: CubicPair[];

	/**
	 * Creates a Morph between two shapes.
	 * @param start - Starting shape
	 * @param end - Ending shape
	 */
	constructor(
		private readonly start: RoundedPolygon,
		private readonly end: RoundedPolygon,
	) {
		this.morphMatch = Morph.match(start, end);
	}

	/**
	 * Returns the interpolated shape at the given progress as a list of Cubics.
	 *
	 * Note: This allocates a new list. For performance-critical animation loops,
	 * use {@link forEachCubic} instead.
	 *
	 * @param progress - Value in [0, 1]. 0 = start shape, 1 = end shape.
	 *   Values outside [0, 1] produce exaggerated shapes (useful for bounce/overshoot).
	 * @returns List of interpolated Cubic curves forming the morphed shape
	 */
	asCubics(progress: number): Cubic[] {
		const result: Cubic[] = [];

		for (const [c1, c2] of this.morphMatch) {
			result.push(
				new Cubic(
					c1.points.map((v, i) => interpolate(v, c2.points[i], progress)),
				),
			);
		}

		return result;
	}

	/**
	 * Iterates over the morphed cubics without allocating new Cubic instances.
	 * Reuses a single MutableCubic for each callback invocation.
	 *
	 * @param progress - Value in [0, 1]
	 * @param callback - Called for each interpolated cubic
	 *
	 * @example
	 * const mutable = new MutableCubic();
	 * morph.forEachCubic(progress, mutable, (c) => {
	 *   path.bezierCurveTo(c.control0X, c.control0Y, c.control1X, c.control1Y, c.anchor1X, c.anchor1Y);
	 * });
	 */
	forEachCubic(
		progress: number,
		mutableOrCallback: MutableCubic | ((c: MutableCubic) => void),
		callback?: (c: MutableCubic) => void,
	): void {
		let mutable: MutableCubic;
		let cb: (c: MutableCubic) => void;

		if (typeof mutableOrCallback === "function") {
			mutable = new MutableCubic();
			cb = mutableOrCallback;
		} else {
			mutable = mutableOrCallback;
			if (!callback) {
				throw new Error(
					"Callback must be provided when passing a mutable cubic",
				);
			}
			cb = callback;
		}

		for (const [c1, c2] of this.morphMatch) {
			mutable.interpolate(c1, c2, progress);
			cb(mutable);
		}
	}

	/**
	 * Calculates the axis-aligned bounding box of the morph (union of start + end bounds).
	 * @param approximate - Use faster approximate bounds (default: true)
	 */
	calculateBounds(approximate = true): [number, number, number, number] {
		const [s0, s1, s2, s3] = this.start.calculateBounds(approximate);
		const [e0, e1, e2, e3] = this.end.calculateBounds(approximate);
		return [
			Math.min(s0, e0),
			Math.min(s1, e1),
			Math.max(s2, e2),
			Math.max(s3, e3),
		];
	}

	/**
	 * Calculates the max bounding square (union of start + end max bounds).
	 */
	calculateMaxBounds(): [number, number, number, number] {
		const [s0, s1, s2, s3] = this.start.calculateMaxBounds();
		const [e0, e1, e2, e3] = this.end.calculateMaxBounds();
		return [
			Math.min(s0, e0),
			Math.min(s1, e1),
			Math.max(s2, e2),
			Math.max(s3, e3),
		];
	}

	// ─── Internal: matching algorithm ──────────────────────────────────────

	/**
	 * Matches cubic curves between two polygons.
	 *
	 * Algorithm:
	 * 1. Measure both polygons (arc-length normalized progress)
	 * 2. Use featureMapper to determine correspondence between features
	 * 3. Cut and shift polygon2 so both start at the same logical point
	 * 4. Walk both lists together, splitting curves when one extends past the other
	 *
	 * @internal
	 */
	private static match(p1: RoundedPolygon, p2: RoundedPolygon): CubicPair[] {
		const measured1 = MeasuredPolygon.measurePolygon(p1);
		const measured2 = MeasuredPolygon.measurePolygon(p2);

		const doubleMapper = featureMapper(measured1.features, measured2.features);
		const polygon2CutPoint = doubleMapper.map(0);

		const bs1 = measured1;
		const bs2 = measured2.cutAndShift(polygon2CutPoint);

		const result: CubicPair[] = [];
		let i1 = 0,
			i2 = 0;
		let b1 = bs1.getOrNull(i1++);
		let b2 = bs2.getOrNull(i2++);

		while (b1 !== null && b2 !== null) {
			const b1a = i1 >= bs1.size ? 1 : b1.endOutlineProgress;
			const b2aRaw =
				i2 >= bs2.size
					? 1
					: positiveModulo(b2.endOutlineProgress + polygon2CutPoint, 1);
			const b2a = doubleMapper.mapBack(b2aRaw);

			const minb = Math.min(b1a, b2a);

			let seg1 = b1,
				newb1 = bs1.getOrNull(i1);
			let seg2 = b2,
				newb2 = bs2.getOrNull(i2);

			if (b1a > minb + ANGLE_EPSILON) {
				const [cut, rest] = bs1.cutAtProgress(b1, minb);
				seg1 = cut;
				newb1 = rest;
			} else {
				i1++;
			}

			if (b2a > minb + ANGLE_EPSILON) {
				const targetProgress = positiveModulo(
					doubleMapper.map(minb) - polygon2CutPoint,
					1,
				);
				const [cut, rest] = bs2.cutAtProgress(b2, targetProgress);
				seg2 = cut;
				newb2 = rest;
			} else {
				i2++;
			}

			result.push([seg1.cubic, seg2.cubic]);
			b1 = newb1;
			b2 = newb2;
		}

		return result;
	}
}
